import { useApolloClient } from '@provision/graphql-client';
import { CgCustomDomainStatus } from '@provision/landing-page-utils';
import { useCallback } from 'react';
import { GET_CG_CUSTOM_DOMAINS_QUERY } from 'src/graphql/entities/cg-custom-domain/cg-custom-domain.operations';
import { useCgCustomDomains } from 'src/graphql/entities/cg-custom-domain/use-cg-custom-domain';
import { GET_CLIENT_GATEWAY_URL_QUERY } from 'src/graphql/entities/client-gateway-url/client-gateway-url.operations';
import { useClientGatewayUrl } from 'src/graphql/entities/client-gateway-url/use-client-gateway-url';
import type { LiveLinkLookup } from 'src/pages/Settings/memberships/hooks/use-live-link-state';

interface SegmentBaseUrl extends LiveLinkLookup {
  url?: string;
}

/**
 * Where clients reach a segment's pages: its active custom domain, else the partner's Client
 * Gateway address. One lookup over both queries, since either failing leaves the address unknown.
 */
export const useSegmentBaseUrl = (
  partnerId: number | null,
  brandId: string | undefined,
): SegmentBaseUrl => {
  const gateway = useClientGatewayUrl(partnerId);
  const customDomains = useCgCustomDomains(partnerId);
  const refetchGateway = gateway.refetch;
  const refetchCustomDomains = customDomains.refetch;
  const refetch = useCallback(
    () => Promise.all([refetchGateway(), refetchCustomDomains()]),
    [refetchGateway, refetchCustomDomains],
  );

  const hostname = customDomains.domains.find(
    (domain) =>
      domain.status === CgCustomDomainStatus.Active &&
      brandId !== undefined &&
      domain.brandIds.includes(brandId),
  )?.hostname;

  return {
    url: hostname ? `https://${hostname}` : (gateway.url ?? undefined),
    loading: gateway.loading || customDomains.loading,
    error: gateway.error ?? customDomains.error,
    refetch,
  };
};

/** A save is when the partner looks at the link, so it rechecks the address too. */
export const useRefetchSegmentBaseUrl = () => {
  const client = useApolloClient();

  return useCallback(
    () =>
      client
        .refetchQueries({
          include: [GET_CLIENT_GATEWAY_URL_QUERY, GET_CG_CUSTOM_DOMAINS_QUERY],
        })
        // A failure shows in the live link, which retries on its own.
        .catch(() => undefined),
    [client],
  );
};
